// 261005 E1-P3b — hàm THUẦN của chặng ③ Bản địa hoá (3 thẻ cách bản địa hoá · bối cảnh phim · từ vựng · cổng AI).
// KHÔNG import antd / react (jest của react-scripts không load được antd v6).
import type { FilmGlossaryDecisionKind, FilmGlossaryTerm, FilmGlossaryTermDecision, LocalizeMode, LocalizeView, WorldSettingLite } from '../../../types/adaptation';
import { buildPolicyBody } from '../adaptationUtils';
import type { DangerCopyView } from './workbenchView';
import { redactMoney } from './moneyRedact';

export interface PolicyCard { mode: LocalizeMode; title: string; hint: string }

/** 3 thẻ theo mockup — KHÔNG hiện mã máy KEEP/MAP/WESTERNIZE ở nhãn. */
export const POLICY_CARDS: PolicyCard[] = [
  { mode: 'KEEP', title: 'Giữ văn hoá gốc', hint: 'Giữ nguyên tên, bối cảnh và phong tục như trong truyện.' },
  { mode: 'MAP', title: 'Chuyển sang văn hoá khác', hint: 'Đổi toàn bộ sang một nền văn hoá bạn chọn.' },
  { mode: 'WESTERNIZE', title: 'Đổi sang phương Tây', hint: 'Tên, bối cảnh, phong tục theo phương Tây — thời đại hợp với truyện (hiện đại hoặc cổ).' },
];

export const policyModeLabel = (mode: LocalizeMode): string => POLICY_CARDS.find((c) => c.mode === mode)?.title ?? String(mode);

export interface PolicyKey { mode: LocalizeMode; targetCulture?: string }

/** Dòng "Đang ghim: …". `implicit` (chưa ghim) ⇒ đang dùng Đổi sang phương Tây — `view.policy` luôn có mặt nên PHẢI nhìn `implicit`. */
export const pinnedLine = (p: { policy?: PolicyKey | null; implicit?: boolean }): string => {
  if (p.implicit || !p.policy) return `Chưa ghim — đang dùng ${policyModeLabel('WESTERNIZE')}.`;
  return `Đang ghim: ${policyModeLabel(p.policy.mode)}${p.policy.mode === 'MAP' && p.policy.targetCulture ? ` → ${p.policy.targetCulture}` : ''}`;
};

export interface PolicyDraftState { dirty: boolean; canSave: boolean; reason?: string; needsCulture: boolean }

/** Bản nháp so với cái đang ghim (`pinned` = `implicit ? undefined : view.policy`; chưa ghim coi như WESTERNIZE). */
export const policyDraftState = (p: {
  draftMode: LocalizeMode; draftCulture: string; pinned?: PolicyKey | null; implicit?: boolean;
}): PolicyDraftState => {
  const cur: PolicyKey = p.implicit || !p.pinned ? { mode: 'WESTERNIZE' } : p.pinned;
  const needsCulture = p.draftMode === 'MAP';
  const dirty = p.draftMode !== cur.mode
    || (p.draftMode === 'MAP' && p.draftCulture.trim() !== (cur.targetCulture ?? '').trim());
  if (!dirty) return { dirty: false, canSave: false, needsCulture };
  const built = buildPolicyBody(p.draftMode, p.draftCulture);
  if (built.ok === false) return { dirty: true, canSave: false, reason: built.reason, needsCulture };
  return { dirty: true, canSave: true, needsCulture };
};

/** Hộp cho 409 LOCALIZE_POLICY_LOCKED (chỉ xảy ra khi đã chạy). */
export const policyLockedCopy = (p: { fromMode: LocalizeMode; toMode: LocalizeMode }): DangerCopyView => ({
  title: 'Đã có kết quả bản địa hoá — đổi cách bản địa hoá?',
  danger: true, okText: 'Vẫn đổi',
  content: `Đổi từ “${policyModeLabel(p.fromMode)}” sang “${policyModeLabel(p.toMode)}”. Tên và từ vựng hiện tại giữ nguyên nhưng thuộc cách cũ; muốn áp cách mới phải bấm Làm lại từ đầu (dùng AI).`,
});

export interface WorldRow { label: string; value: string }
export const worldRows = (ws: WorldSettingLite | null | undefined): { rows: WorldRow[]; empty: boolean } => {
  const v = (s?: string | null) => (s ?? '').toString().trim();
  const rows: WorldRow[] = [
    { label: 'Thời đại', value: v(ws?.era) || '—' },
    { label: 'Thế giới', value: v(ws?.world) || '—' },
    { label: 'Kiến trúc', value: v(ws?.architecture) || '—' },
    { label: 'Trang phục', value: v(ws?.costume) || '—' },
  ];
  return { rows, empty: rows.every((r) => r.value === '—') };
};

export const glossaryStatusLabel = (status: FilmGlossaryTerm['status'] | string): string =>
  status === 'APPROVED' ? 'Đã chấp nhận' : 'Chờ chấp nhận';

// ── 261023 E3-P3h4b1 — bảng từ vựng ③: «Cần bạn» (mặc định) / «Máy tự nhận» / «Đã chấp nhận» + lý do ─────────────────────────────
// BE gửi `termDecisions` (luật tất định); FE chỉ nhóm/lọc/hiển thị. BE cũ không gửi ⇒ `enabled=false` ⇒ giữ nguyên bảng cũ (đủ dòng, không lọc).
// Nhóm KHÔNG chặn việc viết/đẩy. Lý do là chuỗi động ⇒ qua `redactMoney`. KHÔNG hiện `reasonCode`.

export const TERM_DECISION_LABEL: Record<FilmGlossaryDecisionKind, string> = {
  NEEDS_REVIEW: 'Cần bạn', AUTO: 'Máy tự nhận', ACCEPTED: 'Đã chấp nhận',
};
export type TermFilterKey = FilmGlossaryDecisionKind | 'ALL';
export const DEFAULT_TERM_FILTER: TermFilterKey = 'NEEDS_REVIEW';
const TERM_FALLBACK_REASON = 'Máy chưa có căn cứ để tự nhận từ này — bạn xem giúp.';

export interface TermRowView { term: FilmGlossaryTerm; decision: FilmGlossaryDecisionKind | null; label: string; reason: string }
export interface TermCounts { NEEDS_REVIEW: number; AUTO: number; ACCEPTED: number; total: number }
export interface GlossaryTermsView { enabled: boolean; rows: TermRowView[]; counts: TermCounts; emptyText: string | null }

const asDecision = (v: unknown): FilmGlossaryDecisionKind =>
  v === 'ACCEPTED' || v === 'AUTO' || v === 'NEEDS_REVIEW' ? v : 'NEEDS_REVIEW';

/**
 * Nhóm từ vựng theo quyết định của BE. `decisions` vắng/rỗng ⇒ BE cũ ⇒ `enabled=false`, mọi dòng giữ nguyên (không nhãn, không lý do).
 * Từ thiếu quyết định / quyết định lạ ⇒ «Cần bạn» (KHÔNG đoán «Máy tự nhận» theo `status`).
 */
export const glossaryTermsView = (
  glossary: FilmGlossaryTerm[] | null | undefined,
  decisions: FilmGlossaryTermDecision[] | null | undefined,
  filter: TermFilterKey,
): GlossaryTermsView => {
  const terms = glossary ?? [];
  const counts: TermCounts = { NEEDS_REVIEW: 0, AUTO: 0, ACCEPTED: 0, total: terms.length };
  if (!Array.isArray(decisions) || !decisions.length) {
    return { enabled: false, counts, emptyText: null, rows: terms.map((term) => ({ term, decision: null, label: '', reason: '' })) };
  }
  const byId = new Map(decisions.map((d) => [d.termId, d]));
  const all: TermRowView[] = terms.map((term) => {
    const d = byId.get(term.id);
    const decision = asDecision(d?.decision);
    counts[decision] += 1;
    return { term, decision, label: TERM_DECISION_LABEL[decision], reason: redactMoney(d?.reason?.trim() || TERM_FALLBACK_REASON) };
  });
  const rows = filter === 'ALL' ? all : all.filter((r) => r.decision === filter);
  const emptyText = rows.length ? null
    : filter === 'NEEDS_REVIEW' ? 'Không còn từ nào chờ bạn.'
      : filter === 'AUTO' ? 'Chưa có từ nào máy tự nhận.'
        : filter === 'ACCEPTED' ? 'Chưa có từ nào bạn đã chấp nhận.' : 'Chưa có từ vựng.';
  return { enabled: true, rows, counts, emptyText };
};

export const termFilterOptions = (c: TermCounts): Array<{ value: TermFilterKey; label: string }> => [
  { value: 'NEEDS_REVIEW', label: `${TERM_DECISION_LABEL.NEEDS_REVIEW} ${c.NEEDS_REVIEW}` },
  { value: 'AUTO', label: `${TERM_DECISION_LABEL.AUTO} ${c.AUTO}` },
  { value: 'ACCEPTED', label: `${TERM_DECISION_LABEL.ACCEPTED} ${c.ACCEPTED}` },
  { value: 'ALL', label: `Tất cả ${c.total}` },
];

/** «Chấp nhận hết» vẫn khoá MỌI từ `status=AUTO` (nút cũ, BE không đổi) ⇒ nói rõ khi trong đó có từ máy tự nhận. */
export const acceptAllCopy = (pending: number, machineAuto: number): string =>
  machineAuto > 0
    ? `Chấp nhận hết ${pending} từ chờ, gồm cả ${machineAuto} từ máy tự nhận?`
    : `Chấp nhận hết ${pending} từ chờ?`;

/** Cổng AI cần người xử (BLOCKED/FAILED/SKIPPED*) ⇒ phải nằm NGOÀI phần gập. */
export const gateNeedsAttention = (state: string | null | undefined): boolean =>
  state === 'BLOCKED' || state === 'FAILED' || state === 'SKIPPED_BUDGET' || state === 'SKIPPED_OFF';

// ── 261015 P5a (D6) — bối cảnh KEEP/MAP: AI tự ghi khi an toàn / chờ duyệt ─────────────────────────────

export const WORLD_AUTO_TITLE = 'Đã tự ghi bối cảnh (AI) — chưa có tập nào viết';
export const REVERT_WORLD_CONFIRM_TITLE = 'Bỏ bối cảnh AI vừa ghi? Bối cảnh trở về chờ duyệt.';
const WORLD_AUTO_WRITTEN_TITLE = 'Đã tự ghi bối cảnh (AI) — nay đã có tập kịch bản';
const REVERT_BLOCKED_BY_WRITTEN = 'Đã có tập kịch bản — không hoàn tác được; dùng "Sửa" để chỉnh bối cảnh.';

export interface WorldAutoCardView { title: string; at: string; canRevert: boolean; revertDisabledReason: string | null }

/**
 * Thẻ "Đã tự ghi bối cảnh (AI)": CHỈ khi view mang `worldAuto` (BE ghi cho KEEP/MAP) — FE không suy từ `world_setting.source` (WESTERNIZE cũng `AI_AUTO` mà không có Hoàn tác).
 * Tiêu đề "chưa có tập nào viết" chỉ đúng khi truyện vẫn chưa có tập; sau đó Hoàn tác tắt (BE 409 `HAS_WRITTEN_EPISODES`) — chỉ còn "Sửa".
 */
export const worldAutoCardView = (view: LocalizeView | null | undefined, o: { hasWrittenEpisodes: boolean }): WorldAutoCardView | null => {
  const a = view?.worldAuto;
  if (!a || typeof a !== 'object' || !a.proposal) return null;
  return o.hasWrittenEpisodes
    ? { title: WORLD_AUTO_WRITTEN_TITLE, at: String(a.at ?? ''), canRevert: false, revertDisabledReason: REVERT_BLOCKED_BY_WRITTEN }
    : { title: WORLD_AUTO_TITLE, at: String(a.at ?? ''), canRevert: true, revertDisabledReason: null };
};

/** Khối "Bối cảnh phim" vẽ gì: đề xuất chờ duyệt thắng (người cần nút Duyệt) → thẻ tự ghi → thường. */
export const worldBlockMode = (view: LocalizeView | null | undefined, o: { hasWrittenEpisodes: boolean }): 'proposed' | 'auto' | 'plain' => {
  if (view?.worldSettingStatus === 'PROPOSED' && view.proposedWorldSetting) return 'proposed';
  return worldAutoCardView(view, o) ? 'auto' : 'plain';
};

/** Nút ở mỗi hàng cảnh báo của ③: `WORLD_PROPOSAL_PENDING` ⇒ Duyệt (AI không có lệnh sửa gate này nên KHÔNG "Phương án"); đề xuất không dùng được ⇒ không nút. */
export const localizeIssueAction = (gate: string, view: LocalizeView | null | undefined): 'ACCEPT_WORLD' | 'OPTIONS' | 'NONE' => {
  if (gate !== 'WORLD_PROPOSAL_PENDING') return 'OPTIONS';
  return view?.worldSettingStatus === 'PROPOSED' && view.proposedWorldSetting ? 'ACCEPT_WORLD' : 'NONE';
};

/** Cảnh báo kèm theo PUT policy / accept-world: có `kind` thì `notice` ⇒ báo thường (info); thiếu `kind` / loại khác ⇒ giữ `warning` như cũ. */
export const warningToastLevel = (kind: string | undefined | null): 'info' | 'warning' => (kind === 'notice' ? 'info' : 'warning');
